import React from 'react';
import type {
  FormField,
  DualSlotValue,
  GroupedImageSelectorValue,
  VisualSelectOption
} from '../../../../types/templates';
import type { VideoUrl } from '../../../../types/banner/video-url';
import type { FormattedText } from '../../../../types/banner/formatted-text';
import {
  StringDisplay,
  NumberDisplay,
  BooleanDisplay,
  FormattedTextDisplay,
  VideoDisplay,
  ImageDisplay,
  DualSlotDisplay,
  TagListDisplay,
  GroupedSelectionDisplay,
  FallbackDisplay
} from './field-displays';

// Renders a config value using the display that matches its FormField type.
export const FieldDisplay: React.FC<{ field: FormField | undefined; value: unknown }> = ({ field, value }) => {
  if (!field) return <FallbackDisplay value={value} />;

  switch (field.type) {
    case 'input':
    case 'select':
    case 'visual-search-single':
    case 'calendar':
    case 'time':
      return <StringDisplay value={String(value ?? '')} />;
    case 'number':
      return <NumberDisplay value={Number(value ?? 0)} />;
    case 'switch':
      return <BooleanDisplay value={value as boolean | 'si' | 'no'} />;
    case 'rich-text':
      return <FormattedTextDisplay value={value as FormattedText} />;
    case 'video':
      return <VideoDisplay value={value as VideoUrl} />;
    case 'image':
      return <ImageDisplay value={value as string} />;
    case 'visual-select': {
      const opt = value as VisualSelectOption | undefined;
      return <StringDisplay value={opt?.label ?? opt?.value ?? ''} />;
    }
    case 'image-multi-select': {
      const opts = (value as VisualSelectOption[] | undefined) ?? [];
      return <TagListDisplay value={opts.map(o => o.label ?? o.value)} />;
    }
    case 'visual-search':
      return <DualSlotDisplay value={value as DualSlotValue} />;
    case 'grouped-image-selector':
      return <GroupedSelectionDisplay value={(value as GroupedImageSelectorValue) ?? []} />;
    case 'internal':
      return null;
    default:
      return <FallbackDisplay value={value} />;
  }
};
